import { CaseType, MatrixType, OrderType, ProductGender } from '@onrunning/backstage-models';

import type { MatrixProduct } from '@/models/matrix-product.model';
import { useCaseStore } from '@/stores/case';
import { useOrderStore } from '@/stores/order';
import { useProductMatrixStore } from '@/stores/product-matrix';
import type { MatrixOrderProduct } from '@/types/product.interface';
import { computed } from 'vue';

export function useMatrixProduct() {
  const productMatrixStore = useProductMatrixStore();
  const orderStore = useOrderStore();
  const caseStore = useCaseStore();

  const isNewOrder = computed(() => !productMatrixStore.activeMatrixOrder.orderNumber);

  const matrixType = computed<MatrixType>(() => {
    if (orderStore.orderType) {
      switch (orderStore.orderType) {
        case OrderType.LastSeason:
          return MatrixType.lastSeason;
        case OrderType.ReOrder:
        case OrderType.DropShip:
          return MatrixType.order;
        default:
          return MatrixType.preorder;
      }
    } else if (caseStore.caseType) {
      return caseStore.caseType === CaseType.Warranty ? MatrixType.warranty : MatrixType.return;
    }

    return MatrixType.preorder;
  });

  /**
   * Handles quantity change for a single matrix product.
   */
  function onProductQuantityChange(product: MatrixProduct, quantity: number) {
    const currentMatrixOrderTotal = productMatrixStore.activeMatrixOrder.total;
    const products = { ...productMatrixStore.activeMatrixOrder.matrixProducts };

    const existingProduct = products[product.productId];

    // No change, skip update
    if (existingProduct && existingProduct.quantity === quantity) return;

    // Remove product if quantity is 0 and it's a new order/draft
    if (existingProduct && quantity === 0 && isNewOrder.value) {
      delete products[product.productId];

      productMatrixStore.updateMatrixOrder(
        { matrixProducts: products, total: currentMatrixOrderTotal - existingProduct.quantity },
        productMatrixStore.activeMatrixOrderIndex,
      );

      return;
    }

    // Update product quantity if product exists
    if (existingProduct) {
      products[product.productId] = { ...product, quantity };

      productMatrixStore.updateMatrixOrder(
        { matrixProducts: products, total: currentMatrixOrderTotal + quantity - existingProduct.quantity },
        productMatrixStore.activeMatrixOrderIndex,
      );

      return;
    }

    // Add product if not present and quantity > 0
    if (!existingProduct && quantity > 0) {
      products[product.productId] = { ...product, quantity };

      productMatrixStore.updateMatrixOrder(
        { matrixProducts: products, total: currentMatrixOrderTotal + quantity },
        productMatrixStore.activeMatrixOrderIndex,
      );
    }
  }

  /**
   * Removes quantities for a group of products by gender and group name.
   */
  function onRemoveProductGroupQuantities(gender: ProductGender, productGroupName: string) {
    let matrixTotal = productMatrixStore.activeMatrixOrder.total;

    const matrixProducts = { ...productMatrixStore.activeMatrixOrder.matrixProducts };
    const updatedProducts: Record<MatrixProduct['productId'], MatrixOrderProduct> = {};

    for (const productId in matrixProducts) {
      const matrixProduct = matrixProducts[productId];
      const product = productMatrixStore.products[productId];
      const isProduct = !!product && product.gender === gender && product.productGroupName === productGroupName;

      if (isProduct) {
        matrixTotal -= matrixProduct.quantity;
        if (!isNewOrder.value) {
          updatedProducts[productId] = { ...matrixProduct, quantity: 0 };
        }
      } else {
        updatedProducts[productId] = matrixProduct;
      }
    }

    productMatrixStore.updateMatrixOrder(
      { matrixProducts: updatedProducts, total: matrixTotal },
      productMatrixStore.activeMatrixOrderIndex,
    );
  }

  function updateMatrixTotal(total: number) {
    productMatrixStore.setMatrixOrderTotal(total, productMatrixStore.activeMatrixOrderIndex);
  }

  return {
    updateMatrixTotal,
    onProductQuantityChange,
    onRemoveProductGroupQuantities,
    matrixType,
  };
}
